<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Customizations/Editor_Customization';
    let nextLink= '/Documentation/Customizations/Scoper_Customization';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="external-components-1"
  bind:intersecting={visibleById["external-components-1"]}
  onIntersectChange={(visible) => setVisible("external-components-1", visible)}
>External Components</SectionComponent>
<Note>{#snippet header()}External components are fully supported{/snippet}
{#snippet content()}
<p>External components are now a fully supported part of the editor framework. 
While compatibility with every third-party library cannot be guaranteed, the APIs described here are stable.</p>
{/snippet}
</Note>
<p>You can include UI components that are not native to Freon. For example, you can define your own components
or use components from an existing UI library. Freon is built on the</p>
<a href="https://svelte.dev/" target="_blank">Svelte</a> framework, so external components must either be Svelte components
or components wrapped in Svelte.
<p>In general, there are three forms in which an external component can be present in a Freon projection:</p>
<ul>
<li><strong>Simple additions</strong> — an independent external component is added to a projection.</li>
<li><strong>Wrappers</strong> — a property or <a href="/Documentation/Defining_an_Editor/Fragments">fragment</a> is displayed inside an external component.</li>
<li><strong>Replacers</strong> — a property is replaced by an external component (fragments cannot be replaced).<br>
You are responsible for reading and writing property values correctly, and for handling keyboard and tab behavior.</li>
</ul>
<p>Especially the two latter cases require knowledge of the Freon <a
  href="/Documentation/Under_the_Hood/Editor_Framework"
>Box Model</a>.
Please become familiar with that topic first, as below we will refer to the different types of boxes associated with
the various forms of external components. More information on the API of these box types can be
found in <a
  href="/Documentation/Under_the_Hood/Editor_Framework/External_Component_Box_Types"
>External Component Box Types</a>.</p>
<SectionComponent tag="h2" 
  id="more-information-and-assistance-2"
  bind:intersecting={visibleById["more-information-and-assistance-2"]}
  onIntersectChange={(visible) => setVisible("more-information-and-assistance-2", visible)}
>More Information and Assistance</SectionComponent>
<p>This page provides the essentials; a more extensive example is available in the
<a
  href="/Examples/External_Components"
>Examples</a> section.<br>
Anyone interested in using external components can contact the Freon team for assistance.<br>
Please email us at <a href="mailto:info@openmodeling.nl">info@openmodeling.nl</a> to stay updated on the latest news about this feature.</p>
<SectionComponent tag="h2" 
  id="simple-additions-3"
  bind:intersecting={visibleById["simple-additions-3"]}
  onIntersectChange={(visible) => setVisible("simple-additions-3", visible)}
>Simple Additions</SectionComponent>
<p>Simple additions have no link to the model (the AST). They may appear anywhere in the projection.
To include a simple external component within a projection, use the syntax <code>[external=&lt;COMPONENT_NAME&gt;]</code>,
where <code>&lt;COMPONENT_NAME&gt;</code> is the name of the desired component.<br>
Ensure there is no space between the opening bracket (<code>[</code>) and the keyword <code>external</code>.<br>
The component name must be included in the <a
  href="/Documentation/Defining_an_Editor/Global_Projections"
><code>global</code></a> section of the
default editor.<br>
The associated box type is <code>SimpleExternalBox</code>.</p>
<p>In the next example a simple animated gif component is added to the fragment projection.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-externals.edit#L10-L16

First Card
    is still under construction: $&#123;self.isUnderConstruction switch&#125;
    is approved level1: $&#123;self.isApprovedLevel1 radio&#125;
    is approved level2: $&#123;self.isApprovedLevel2 inner-switch&#125;
    is approved level3: $&#123;self.isApprovedLevel3 checkbox&#125;
    [external=AnimatedGif number="1"]
]</code></pre>
<Figure
imageName={'documentation/Documentation-Externals-screenshot1.png'}
caption={'Adding an animated gif'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="parameters-and-required-functions-4"
  bind:intersecting={visibleById["parameters-and-required-functions-4"]}
  onIntersectChange={(visible) => setVisible("parameters-and-required-functions-4", visible)}
>Parameters and Required Functions</SectionComponent>
<p>You can set parameters to an external component in the <code>.edit</code> file. These are simple key-value pairs (both key
and value are strings). There can be a list of them.<br>
All box types for external components include the method <code>findParam(key: string): string</code><br>
to retrieve parameter values from the <code>.edit</code> file.</p>
<p>Every external component receives two props and should define the following methods/hooks to integrate with Freon:</p>
<pre class="language-ts"><code class="language-ts">let &#123; editor, box &#125;: FreComponentProps&lt;BOXTYPE&gt; = $props();</code></pre>
<ul>
<li><code>box.setFocus</code> — sets the focus on the first selectable element (optional if nothing is focusable).</li>
<li><code>box.refreshComponent</code> — called when the underlying model changes and the UI needs to update.</li>
<li><code>onMount</code> — runs when the component is mounted.</li>
<li><code>afterUpdate</code> — runs after the DOM is updated.</li>
</ul>
<p>In the example below, the parameter is a number used to select an image source.<br>
The source code of the <code>AnimatedGif</code> Svelte component is shown next.</p>
<pre class="language-svelte"><code class="language-svelte">// Insurance/src/external/ShowAnimatedGif.svelte

&lt;script lang="ts"&gt;

    import &#123;FragmentWrapperBox, isNullOrUndefined&#125; from "@freon4dsl/core";
    import type &#123; FreComponentProps &#125; from "@freon4dsl/core-svelte";

    let src1 = './customImages/cats-kittens.gif';
    let name1 = 'Two kittens licking';
    let src2 = './customImages//rick-roll-rick-rolled.gif';
    let name2 = 'Rick Astley dancing';
    let src3 = './customImages/lenny-confetti-hired-kitten.gif';
    let name3 = 'Staring kitten';

    // Freon expects both of these to be present, even if they are not used.
    // Props
    let &#123; editor, box &#125;: FreComponentProps&lt;FragmentWrapperBox&gt; = $props();

    let src: string = $state(src1);
    let name: string = $state(name1);

    function getSrc() &#123;
        let myParam: string | undefined = box.findParam("number");
        if (!isNullOrUndefined(myParam)) &#123;
            let nrOfSrc: number = Number.parseInt(myParam);
            switch (nrOfSrc) &#123;
                case 1: &#123;
                    src = src1;
                    name = name1;
                    break;
                &#125;
                case 2: &#123;
                    src = src2;
                    name = name2;
                    break;
                &#125;
                case 3: &#123;
                    src = src3;
                    name = name3;
                    break;
                &#125;
            &#125;
        &#125;
    &#125;
    // execute this function to set the initial values
    getSrc();

    // The following two functions need to be included for the editor to function properly.
    // Please, set the focus to the first editable/selectable element in this component.
    // If this element is not focusable, then do not use this function. Freon will direct
    // the focus to the parent of this component.
    // async function setFocus(): Promise&lt;void&gt; &#123;
    // &#125;
    const refresh = (why?: string): void =&gt; &#123;
        // do whatever needs to be done to refresh the elements that show information from the model
        getSrc();
    &#125;;
    $effect(() =&gt; &#123;
        // box.setFocus = setFocus;
        box.refreshComponent = refresh;
    &#125;);

    // execute getSrc on initialization
    getSrc();
&lt;/script&gt;

&lt;!-- &#123;src&#125; is short for src=&#123;src&#125; --&gt;
&lt;img &#123;src&#125; alt="&#123;name&#125;" /&gt;
</code></pre>
<SectionComponent tag="h2" 
  id="wrapping-a-freon-projection-5"
  bind:intersecting={visibleById["wrapping-a-freon-projection-5"]}
  onIntersectChange={(visible) => setVisible("wrapping-a-freon-projection-5", visible)}
>Wrapping a Freon Projection</SectionComponent>
<p>An external component may wrap a Freon projection—either a property projection or
a <a
  href="/Documentation/Defining_an_Editor/Fragments"
>fragment projection</a>.
Wrappers may appear anywhere in a projection.<br>
The wrapped projection is available as the <code>childBox</code> property (<code>Box</code>).<br>
Note that the <code>childBox</code> itself may be a vertical or horizontal layout containing multiple elements.</p>
<p>The syntax is:</p>
<ul>
<li><code>[fragment NAME wrap=ExternalComponent]</code>, where <code>NAME</code> is the fragment name.</li>
<li><code>$&#123;self.PROPERTY wrap=ExternalComponent&#125;</code>, where <code>self.PROPERTY</code> refers to a property.</li>
</ul>
<p>The box type associated with a wrapper depends on the type of property or fragment that is being wrapped<br>
(see <a
  href="/Documentation/Under_the_Hood/Editor_Framework/External_Component_Box_Types"
>External Component Box Types</a>):</p>
<ul>
<li>Fragment: <code>FragmentWrapperBox</code></li>
<li>Property of type string: <code>StringWrapperBox</code></li>
<li>Property of type number: <code>NumberWrapperBox</code></li>
<li>Property of type boolean: <code>BooleanWrapperBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#part-properties-3"
>Part property</a>: <code>PartWrapperBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#part-properties-3"
>Part list property</a>: <code>PartListWrapperBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#reference-properties-4"
>Reference property</a>: <code>RefWrapperBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#reference-properties-4"
>Reference list property</a>: <code>RefListWrapperBox</code></li>
</ul>
<p>To display the <code>childBox</code>, the external component must include the Freon <code>RenderComponent</code>, as shown below.<br>
In this example, a fragment is wrapped in a <code>Card</code> component imported from the</p>
<a href="https://flowbite-svelte.com/" target="_blank">Flowbite Svelte</a> library.
<pre class="language-svelte"><code class="language-svelte">// Insurance/src/external/FB_Card_Component.svelte#L25-L29

&lt;span class="card-container"&gt;
    &lt;Card&gt;
        &lt;RenderComponent box=&#123;box.childBox&#125; editor=&#123;editor&#125; /&gt;
    &lt;/Card&gt;
&lt;/span&gt;</code></pre>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-externals.edit#L6-L6

[fragment FirstCard wrap=ExternalCard] [fragment SecondCard wrap=ExternalCard]</code></pre>
<SectionComponent tag="h2" 
  id="replacing-a-freon-projection-6"
  bind:intersecting={visibleById["replacing-a-freon-projection-6"]}
  onIntersectChange={(visible) => setVisible("replacing-a-freon-projection-6", visible)}
>Replacing a Freon Projection</SectionComponent>
<p>A Freon projection may also be replaced by an external component.<br>
A <a href="/Documentation/Defining_an_Editor/Fragments">fragment projection</a> may <strong>not</strong> be replaced.</p>
<p>The syntax is:</p>
<pre class="language-freon"><code class="language-freon">$&#123;self.PROPERTY replace=ExternalComponent&#125;</code></pre>
<p>The box type associated with a replacement depends on the type of property that is being replaced<br>
(see <a
  href="/Documentation/Under_the_Hood/Editor_Framework/External_Component_Box_Types"
>External Component Box Types</a>):</p>
<ul>
<li>Property of type string: <code>StringReplacerBox</code></li>
<li>Property of type number: <code>NumberReplacerBox</code></li>
<li>Property of type boolean: <code>BooleanReplacerBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#part-properties-3"
>Part property</a>: <code>PartReplacerBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#part-properties-3"
>Part list property</a>: <code>PartListReplacerBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#reference-properties-4"
>Reference property</a>: <code>RefReplacerBox</code></li>
<li><a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#reference-properties-4"
>Reference list property</a>: <code>RefListReplacerBox</code></li>
</ul>
<p>Each box type provides the following methods (where <code>&lt;TYPE&gt;</code> depends on the property type):</p>
<ul>
<li><code>getPropertyName(): string</code> — returns the name of the wrapped property.</li>
<li><code>getPropertyValue(): &lt;TYPE&gt;</code> — returns the current property value.</li>
<li><code>setPropertyValue(newValue: &lt;TYPE&gt;)</code> — sets the property value.</li>
</ul>
<p>In the following example the <code>name</code> property of a <code>BaseProduct</code> is displayed using a dialog component
from the <a href="https://flowbite-svelte.com/" target="_blank">Flowbite Svelte</a> UI library.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-externals.edit#L5-L5

Base Product for $&#123;self.theme radio&#125; $&#123;self.name replace=ExternalDialog buttonLabel = "Change Product Name"&#125;</code></pre>
<Figure
imageName={'documentation/Documentation-Externals-screenshot2.png'}
caption={'Replacing a string property with a dialog'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="wiring-7"
  bind:intersecting={visibleById["wiring-7"]}
  onIntersectChange={(visible) => setVisible("wiring-7", visible)}
>Wiring</SectionComponent>
<p>The setup for including external components requires some care.</p>
<ol>
<li><strong>Create your Svelte components.</strong><br>
Note which box type your component will link to, and use its interface to get and set model values.</li>
<li><strong>Declare external components in the editor definition.</strong><br>
This is done in the <code>global</code> section of the default editor<br>
(see <a
  href="/Documentation/Defining_an_Editor/Global_Projections"
>Global Projections</a>).</li>
<li><strong>Register external components at runtime.</strong><br>
Use <code>setCustomComponents()</code> from the <code>@freon4dsl/core-svelte</code> package.<br>
Call this method in <code>src/starter.ts</code> before launching the app.<br>
The component names must match those declared in step 2.</li>
</ol>
<p>Example:</p>
<pre class="language-ts"><code class="language-ts">setCustomComponents([
   &#123; component: ShowAnimatedGif, knownAs: "AnimatedGif" &#125;,
   &#123; component: FB_Card_Component, knownAs: "FB_Card" &#125;,
   &#123; component: FB_Accordion, knownAs: "FB_Accordion" &#125;,
   &#123; component: FB_Dialog, knownAs: "FB_Dialog" &#125;,
   &#123; component: DatePicker, knownAs: "DatePicker" &#125;
]);

const app = new FreonLayout(&#123;
    target: document.body,
&#125;);

export default app;</code></pre>
<SectionComponent tag="h2" 
  id="nesting-8"
  bind:intersecting={visibleById["nesting-8"]}
  onIntersectChange={(visible) => setVisible("nesting-8", visible)}
>Nesting</SectionComponent>
<p>External projections may be nested.<br>
Example:</p>
<pre class="language-freon"><code class="language-freon">[external=FB_Card [
    This Card is showing animated gif number 1.
        [external=AnimatedGif number="1"]
    ]
]</code></pre>


<PrevNextSection {prevLink} {nextLink} />